import { useKeycloak } from 'all-in-one-react-keycloak';
import { Benefit, ClinicOrDoctorSearchResult, useClinicOrDoctorSearch } from 'all-in-one-shared-api';
import { Spin } from 'antd';
import { FormWrapper } from 'components/form';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ClaimInfoCardUtils } from './utils';
import { useFormContext } from 'react-hook-form';
import { isLocaleEnglish } from 'utils/languageUtils';
import { getLocaleName } from 'utils/getLocaleDescription';
import { FormAutoComplete, FormAutoCompleteOption } from '../../../../components/form/FormAutoComplete';
import { findDoctorIdAndNameByQuery } from 'utils/claims';

interface DoctorSelectProps {
  selectedClaimTypeObject: Benefit[];
}

export const DoctorSelect = ({ selectedClaimTypeObject }: DoctorSelectProps) => {
  const { t, i18n } = useTranslation();
  const { initialized, keycloak } = useKeycloak();
  const { setValue, getValues, watch } = useFormContext();
  const doctorName: string = watch('doctorName');
  const doctorNameQuery: string = getValues('doctorNameQuery');
  const claimType = getValues('claimType');
  const claims = getValues('claims');
  const previousClaimTypes = useRef('');
  const doctorFromSelect = useRef<ClinicOrDoctorSearchResult>();
  const isChangeBySelect = useRef(false);

  const specialties = useMemo(() => {
    if (!selectedClaimTypeObject) return '';

    return selectedClaimTypeObject.map((o) => o?.specialty?.split(',')).join(',');
  }, [selectedClaimTypeObject]);

  const toBenefitsString = (benefits: Benefit[]) => {
    return claimType === 'other' ? '' : (benefits || []).map((benefit: Benefit) => benefit.code).join(',');
  };

  const getClaimType = () => {
    // When claimType is other, it only has one benefit, and should be converted to claimType
    return claimType === 'other' ? toBenefitsString(claims) : claimType;
  };

  const {
    data: doctorData,
    shouldFetch,
    isLoading: doctorIsLoading,
  } = useClinicOrDoctorSearch(
    `doctorSearch?${specialties}&${doctorNameQuery}`,
    keycloak.token || '',
    doctorNameQuery,
    specialties,
    getClaimType(),
    toBenefitsString(claims),
    '0',
    '100',
    {
      shouldRetryOnError: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    },
  );

  useEffect(() => {
    shouldFetch(initialized && !!keycloak.authenticated && !!keycloak.token);
  }, [initialized, keycloak.authenticated, keycloak.token, shouldFetch]);

  const isFreetextDoctor = useCallback((): boolean => {
    if (!doctorFromSelect.current) {
      return !!getValues('isFreetextDoctor');
    }
    return doctorFromSelect.current?.name !== doctorName && doctorFromSelect.current?.chineseName !== doctorName;
  }, [doctorName, getValues]);

  const currentClaimsString = useMemo(() => (claims || []).map((claim: Benefit) => claim.code).toString(), [claims]);

  const resetDoctor = useCallback(() => {
    setValue('doctorName', '');
    setValue('doctorId', '');
    setValue('doctorNameQuery', '');
  }, [setValue]);

  const resetDoctorAfterChangeClaimType = useCallback(() => {
    if (!doctorData?.results) {
      return;
    }
    previousClaimTypes.current = currentClaimsString;
    const doctor = findDoctorIdAndNameByQuery(doctorData?.results || [], doctorName);
    if (isFreetextDoctor()) return;

    if (!doctor.id) {
      resetDoctor();
    }
  }, [doctorData?.results, currentClaimsString, doctorName, isFreetextDoctor, resetDoctor]);

  useEffect(() => {
    if (!doctorData?.results) return;

    const doctor = findDoctorIdAndNameByQuery(doctorData?.results, doctorName);

    setValue('doctorId', doctor.id);
    setValue('doctorName', getLocaleName(isLocaleEnglish(i18n.language))(doctor));
    setValue('doctorNameQuery', doctor.name || doctorName || doctorData.request.searchQuery);
  }, [setValue, doctorData?.results, i18n.language, doctorName, doctorData, resetDoctorAfterChangeClaimType]);

  useEffect(() => {
    if (!currentClaimsString && !isFreetextDoctor()) {
      resetDoctor();
    }
    if (!previousClaimTypes.current) {
      previousClaimTypes.current = currentClaimsString;
    }
    if (previousClaimTypes.current !== currentClaimsString) {
      resetDoctorAfterChangeClaimType();
    }
  }, [resetDoctorAfterChangeClaimType, currentClaimsString, isFreetextDoctor, resetDoctor]);

  const handleOnDoctorSearch = (value: string) => {
    setValue('doctorNameQuery', value);

    return doctorData;
  };

  const options = useMemo(() => {
    return doctorData
      ? ClaimInfoCardUtils.formatDoctorData(doctorData, i18n.language).map((doctor) => {
          return {
            value: doctor.id,
            label: <FormAutoCompleteOption name={doctor.name} caption={doctor.caption} />,
          };
        })
      : undefined;
  }, [doctorData, i18n.language]);

  return (
    <FormWrapper name="doctorName" label={t('newClaim.claimInfo.clinicOrDoctorLabel')}>
      <FormAutoComplete
        allowClear
        onSelect={(value: string) => {
          doctorFromSelect.current = findDoctorIdAndNameByQuery(doctorData?.results || [], value);
          isChangeBySelect.current = true;
          setValue('isFreetextDoctor', false);
        }}
        name="doctorName"
        onChange={() => {
          if (!isChangeBySelect.current) {
            setValue('isFreetextDoctor', true);
            isChangeBySelect.current = false;
          }
        }}
        onSearch={handleOnDoctorSearch}
        showSearch
        placeholder={t('newClaim.claimInfo.clinicOrDoctorExtraText')}
        notFoundContent={doctorIsLoading ? <Spin size="small" /> : null}
        options={options}
        filterOption={false}
      />
    </FormWrapper>
  );
};
